import initSqlJs from "sql.js";
import { loadDbFromIndexedDB, saveDbToIndexedDB } from "../preOffline/indexedDbHelper";

const DB_STORAGE_KEY = "sqljs_db"; // key in localStorage



// Helper function to get table schema from local DB
const getTableSchemaFromLocal = async (db, tableName) => {
  const query = `SELECT * FROM _table_schema WHERE tableName = '${tableName}'`;
  const result = db.exec(query);
  if (!result || result.length === 0) return [];

  const columns = result[0].columns;
  const values = result[0].values;

  return values.map((row) =>
    row.reduce((obj, val, idx) => {
      obj[columns[idx]] = val;
      return obj;
    }, {})
  );
};

// Function to add missing fields based on schema
async function addMissingFieldsFromSchema(data, schemaRows) {
  return schemaRows.reduce((updatedData, { field, nullable, defaultValue }) => {
    if (field !== "id") {
      updatedData[field] =
        field in data ? data[field] : nullable === "YES" ? null : defaultValue;
    }
    return updatedData;
  }, {});
}

async function removeInvalidFieldsFromData(data, schemaRows) {
  const validFields = schemaRows.map(({ field }) => field);
  return Object.keys(data).reduce((filteredData, key) => {
    if (key === "id" || validFields.includes(key)) {
      filteredData[key] = data[key];
    }
    return filteredData;
  }, {});
}

// Function to get table name and ID from URL
const getTableNameAndId = (url) => {
  const parts = url.split("/").filter(Boolean);
  const last = parts[parts.length - 1];
  const tableName = /^\d+$/.test(last) ? parts[parts.length - 2] : last;
  const id = /^\d+$/.test(last) ? last : null;
  return [tableName, id];
};

// Function to ensure table exists
const ensureTableExists = async (db, tableName, finalData) => {
  const columnDefs = Object.keys(finalData)
    .map((col) => `"${col}" TEXT`)
    .join(", ");
  const createSQL = `CREATE TABLE IF NOT EXISTS "${tableName}" (id INTEGER PRIMARY KEY AUTOINCREMENT, ${columnDefs});`;
  db.run(createSQL);
};

// Function to ensure table schema matches the final data
const ensureTableSchema = async (db, tableName, finalData) => {
  const existingCols = new Set(
    db
      .exec(`PRAGMA table_info("${tableName}")`)?.[0]
      ?.values?.map((row) => row[1]) || []
  );
  for (const col of Object.keys(finalData)) {
    if (!existingCols.has(col)) {
      db.run(`ALTER TABLE "${tableName}" ADD COLUMN "${col}" TEXT;`);
    }
  }
};

// Function to build insert SQL
const buildInsertSQL = (tableName, finalData) => {
  const columns = Object.keys(finalData);
  const placeholders = columns.map(() => "?").join(", ");
  const insertSQL = `INSERT INTO "${tableName}" (${columns.join(
    ","
  )}) VALUES (${placeholders})`;
  const params = columns.map((col) =>
    finalData[col] != null ? String(finalData[col]) : ""
  );
  return { insertSQL, params };
};

// Function to build update SQL
const buildUpdateSQL = (tableName, finalData, id) => {
  const columns = Object.keys(finalData);
  const updates = columns.map((col) => `"${col}" = ?`).join(", ");
  const updateSQL = `UPDATE "${tableName}" SET ${updates} WHERE id = ?`;
  const params = [
    ...columns.map((col) =>
      finalData[col] != null ? String(finalData[col]) : ""
    ),
    id,
  ];
  return { updateSQL, params };
};


export const preprocessSqlQuery = (query) => {
  const generateCaseExpression = (cols, isGreatest) => {
    const absCols = cols.map((col) => `abs(${col.trim()})`);
    let caseExpression = "CASE\n";

    absCols.forEach((absCol, idx) => {
      const comparisons = absCols
        .filter((_, j) => j !== idx)
        .map((otherCol) => `${absCol} ${isGreatest ? ">=" : "<="} ${otherCol}`)
        .join(" AND ");
      caseExpression += `  WHEN ${comparisons} THEN ${absCol}\n`;
    });

    caseExpression += `  ELSE ${absCols[0]}\nEND`;
    return caseExpression;
  };

  // Replace greatest(...)
  query = query.replace(
    /greatest\s*\(\s*((?:abs\([^)]+\)\s*,?\s*)+)\)/gi,
    (match, colsStr) => {
      const cols = colsStr.split(/\s*,\s*/).map((absCol) => {
        const innerCol = absCol.match(/abs\(([^)]+)\)/i);
        return innerCol ? innerCol[1] : absCol;
      });
      return generateCaseExpression(cols, true);
    }
  );

  // Replace least(...)
  query = query.replace(
    /least\s*\(\s*((?:abs\([^)]+\)\s*,?\s*)+)\)/gi,
    (match, colsStr) => {
      const cols = colsStr.split(/\s*,\s*/).map((absCol) => {
        const innerCol = absCol.match(/abs\(([^)]+)\)/i);
        return innerCol ? innerCol[1] : absCol;
      });
      return generateCaseExpression(cols, false);
    }
  );

  // Remove or replace STDDEV and VARIANCE with 0
  query = query.replace(/\bSTDDEV\s*\([^)]+\)/gi, "0");
  query = query.replace(/\bVARIANCE\s*\([^)]+\)/gi, "0");

  return query;
};

export const executeSqlInSqlJs = async (query) => {
  try {
    const SQL = await initSqlJs({ locateFile: () => "sql-wasm.wasm" });
    const db = await getDatabaseInstance(SQL);

    if (!db) {
      console.error("Database not found.");
      return { data: { affectedRows: 0, insertId: null, affectedId: null } };
    }

    const isSelect = /^\s*select/i.test(query);
    let result;
    let affectedRows = 0;
    let affectedId = null;


    if (isSelect) {
      result = db.exec(query);
      // For SELECT, affectedRows could be rows returned or 0 if none
      affectedRows = result.length ? result[0].values.length : 0;
    } else {
      // console.log("SQL Query ---->>",query)
      db.run(query);
      affectedRows = db.getRowsModified();

      // For INSERT, try to get last inserted id
      if (/^\s*insert/i.test(query)) {
        const res = db.exec("SELECT last_insert_rowid() as id");
        if (res.length && res[0].values.length) {
          affectedId = res[0].values[0][0];
        }
      }
      await saveDatabase(db);
      result = { success: true };
    }

    return {
      data: {
        affectedRows,
        affectedId,
      },
    };
  } catch (error) {
    console.error("Error executing query:", query, error);
    return { data: { affectedRows: 0, insertId: null, affectedId: null } };
  }
};


// Function to save the database back to localStorage
export const saveDatabase = async (db) => {
  const binaryArray = db.export();
  await saveDbToIndexedDB(binaryArray);
  db.close();
};

// Helper function to get or initialize database instance
export const getDatabaseInstance = async (SQL) => {
  const binaryArray = await loadDbFromIndexedDB();

  if (binaryArray) {
    return new SQL.Database(binaryArray);
  }

  return new SQL.Database();
};

// Helper function to filter data keys based on schema fields
const filterDataBySchema = (data, schemaFields) => {
  return data.map((item) => {
    const filteredItem = {};
    schemaFields.forEach((field) => {
      filteredItem[field] = item[field];
    });
    return filteredItem;
  });
};

// Optimized insert function for ducInward using async/await with error handling
const handleDucInwardInsert = async (ducInwardData, schema) => {
  try {
    const filteredArrays = {
      srfInstruments: filterDataBySchema(ducInwardData, schema.srfInstruments),
      datasheets: filterDataBySchema(ducInwardData, schema.datasheet),
      certificates: filterDataBySchema(ducInwardData, schema.certificate),
    };

    for (const tableName of Object.keys(filteredArrays)) {
      for (const rowData of filteredArrays[tableName]) {
        try {
          await createOrUpdateTableData(`/api/${tableName}`, rowData, "post");
        } catch (error) {
          console.error(
            `Error inserting into table ${tableName} with data:`,
            rowData,
            error
          );
          throw error; // Rethrow to stop further processing if needed
        }
      }
    }
  } catch (error) {
    console.error("handleDucInwardInsert failed:", error);
    throw error;
  }
};

// Optimized update function for ducInward using async/await with error handling
const handleDucInwardUpdate = async (ducInwardData, schema) => {
  try {
    const filteredArrays = {
      srfInstruments: filterDataBySchema(ducInwardData, schema.srfInstruments),
      datasheets: filterDataBySchema(ducInwardData, schema.datasheet),
      certificates: filterDataBySchema(ducInwardData, schema.certificate),
    };

    for (const tableName of Object.keys(filteredArrays)) {
      for (const rowData of filteredArrays[tableName]) {
        if (rowData.id && Number(rowData.id) >= 1) {
          try {
            await createOrUpdateTableData(
              `/api/${tableName}/${rowData.id}`,
              rowData,
              "patch"
            );
          } catch (error) {
            console.error(
              `Error updating table ${tableName} with data:`,
              rowData,
              error
            );
            throw error;
          }
        }
      }
    }
  } catch (error) {
    console.error("handleDucInwardUpdate failed:", error);
    throw error;
  }
};

const handleReadingsOperation = async (id, data, db) => {
  let affectedRows = 0;
  let firstInsertId = null;

  try {
    await executeSqlInSqlJs(
      `DELETE FROM datasheetStaticReadings WHERE datasheetId = ${Number(id)}`
    );

    if (Array.isArray(data?.readings)) {
      for (const reading of data.readings) {
        const payload = { ...reading, datasheetId: id };

        try {
          const result = await createOrUpdateTableData(
            `/api/datasheetStaticReadings`,
            payload,
            "post"
          );

          if (result?.data?.insertId != null) {
            if (firstInsertId === null) {
              firstInsertId = result.data.insertId;
            }
            affectedRows++;
          }
        } catch (error) {
          console.error(
            `Error inserting reading for datasheetId ${id}:`,
            payload,
            error
          );
          throw error;
        }
      }
    } else {
      console.warn("No readings found in data.readings");
    }

    return {
      affectedRows,
      insertId: firstInsertId != null ? firstInsertId : null,
    };

  } catch (error) {
    console.error("handleReadingsOperation failed:", error);
    throw error;
  }
};



// Main function: create or update table data with proper async/await and error handling
export const createOrUpdateTableData = async (url, data, method) => {
  try {
    const SQL = await initSqlJs({ locateFile: (file) => `sql-wasm.wasm` });
    let db = await getDatabaseInstance(SQL);

    const [tableName, id] = getTableNameAndId(url);

    if (tableName === "ducInward" && method === "post") {
      await handleDucInwardInsert(data.ducInwardData, data.schema);
      return { data: { affectedRows: 0, insertId: null } };
    }

    if (tableName === "ducInward" && method === "patch") {
      await handleDucInwardUpdate(data.ducInwardData, data.schema);
      return { data: { affectedRows: 0, insertId: null } };
    }

if (tableName === "readings" && (method === "post" || method === "patch")) {
  const result = await handleReadingsOperation(id, data, db);

  return {
    status: 201,
    data: {
      affectedRows: result.affectedRows,
      insertId: result.insertId,
    },
  };
}



    // Add missing fields and ensure schema
    const schemaRows = await getTableSchemaFromLocal(db, tableName);
    const finalData = await addMissingFieldsFromSchema(data, schemaRows);
    const patchData = await removeInvalidFieldsFromData(data, schemaRows);

    await ensureTableExists(db, tableName, finalData);
    await ensureTableSchema(db, tableName, finalData);

    let affectedRows = 0;
    let insertId = null;

    if (method === "post") {
      const { insertSQL, params } = buildInsertSQL(tableName, finalData);
      const stmt = db.prepare(insertSQL);
      stmt.run(params);
      stmt.free();

     insertId =
    db.exec(`SELECT last_insert_rowid() AS id`)?.[0]?.values?.[0]?.[0] ?? null;
      affectedRows = 1;
    } else if (method === "patch" && id) {
      const { updateSQL, params } = buildUpdateSQL(tableName, patchData, id);
      const stmt = db.prepare(updateSQL);
      stmt.run(params);
      stmt.free();
      affectedRows = db.getRowsModified();
    } else if (method === "delete" && id) {
      db.run(`DELETE FROM "${tableName}" WHERE id = ?`, [id]);
      affectedRows = db.getRowsModified();
    }

    await saveDatabase(db);

    return { data: { affectedRows, insertId } };
  } catch (error) {
    console.error(
      `createOrUpdateTableData failed for ${url} with method ${method}`,
      error
    );
    throw error;
  }
};
